import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { PageHeader, Panel, Field } from "../components/UI";
import { groupInstalledMods, INSTALLATION_GROUPS, resolveInstalledMods } from "../utils/installedMods";
import "./ModsView.css";

const API = "";

export default function ModsView({ setRestartRequired }) {

  const [config, setConfig] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [bulkMods, setBulkMods] = useState("");
  const [intelligence, setIntelligence] = useState({ status: "loading", metadata: new Map(), required: new Set() });
  const [retry, setRetry] = useState(0);
  const workshopCache = useRef(new Map());

  const [newMod, setNewMod] = useState({
    modId: "",
    name: "",
    version: ""
  });

  const fetchConfig = () => {
    axios.get(`${API}/api/config`)
      .then(res => setConfig(res.data))
      .catch(() => {
        alert("Failed to load configuration.");
      });
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const scenarioId = config?.game?.scenarioId;
  const installedMods = config?.game?.mods;
  useEffect(() => {
    if (!Array.isArray(installedMods)) return;
    let current = true;
    setIntelligence({ status: "loading", metadata: new Map(), required: new Set() });
    const cachedGet = url => {
      if (!workshopCache.current.has(url)) {
        const request = axios.get(url).then(response => response.data).catch(error => {
          workshopCache.current.delete(url);
          throw error;
        });
        workshopCache.current.set(url, request);
      }
      return workshopCache.current.get(url);
    };
    resolveInstalledMods({ game: { scenarioId, mods: installedMods } },
      id => cachedGet(`${API}/api/workshop/item/${id}`),
      id => cachedGet(`${API}/api/workshop/item/${id}/dependencies`)
    ).then(result => { if (current) setIntelligence(result); });
    return () => { current = false; };
  }, [scenarioId, installedMods, retry]);

  /* ---------- SINGLE ADD ---------- */

  const addSingleMod = () => {
    if (!newMod.modId || !newMod.name || !newMod.version) {
      alert("All fields are required.");
      return;
    }

    axios.post(`${API}/api/mod/add`, newMod)
      .then(res => {
        setConfig(res.data);
        if (typeof setRestartRequired === "function") { setRestartRequired(true); }
        setNewMod({ modId: "", name: "", version: "" });
      })
      .catch(err => {
        alert(err.response?.data?.error || "Failed to add mod.");
      });
  };

  /* ---------- REMOVE ---------- */

  const removeMod = (modId) => {
    if (!window.confirm("Remove this mod?")) return;

    axios.post(`${API}/api/mod/remove`, { modId })
      .then(res => {
        setConfig(res.data);
        if (typeof setRestartRequired === "function") { setRestartRequired(true); }
      })
      .catch(err => {
        alert(err.response?.data?.error || "Failed to remove mod.");
      });
  };

  const removeAllMods = () => {
    if (!window.confirm("Are you sure you want to REMOVE ALL mods?")) return;

    axios.post(`${API}/api/mod/bulk`, { mods: [] })
      .then(res => {
        setConfig(res.data);
        if (typeof setRestartRequired === "function") { setRestartRequired(true); }
      })
      .catch(err => {
        alert(err.response?.data?.error || "Failed to remove all mods.");
      });
  };

  /* ---------- BULK ---------- */

  const replaceMods = () => {
    try {
      let text = bulkMods.trim();

      if (!text.startsWith("[")) {
        text = `[${text}]`;
      }

      const parsed = JSON.parse(text);

      if (!Array.isArray(parsed)) {
        alert("Invalid format: must be an array of mod objects.");
        return;
      }

      for (const mod of parsed) {
        if (!mod.modId || !mod.name || !mod.version) {
          alert("Each mod must contain modId, name, and version.");
          return;
        }
      }

      if (!window.confirm(`Replace entire mod list with ${parsed.length} mods?`)) return;

      axios.post(`${API}/api/mod/bulk`, { mods: parsed })
        .then(res => {
          setConfig(res.data);
          if (typeof setRestartRequired === "function") { setRestartRequired(true); }
          setBulkMods("");
        })
        .catch(err => {
          alert(err.response?.data?.error || "Bulk update failed.");
        });

    } catch {
      alert("Invalid JSON format.");
    }
  };

  /* ---------- GROUPING ---------- */

  const groupMods = (mods) => {
    const categories = {
      Core: [],
      Weapons: [],
      Vehicles: [],
      Equipment: [],
      Effects: [],
      AI: [],
      Radios: [],
      "Maps / Scenarios": [],
      Other: []
    };

    mods.forEach(mod => {
      const name = (mod.name || "").toLowerCase();

      if (name.includes("core")) categories.Core.push(mod);
      else if (name.includes("weapon") || name.includes("armament") || name.includes("scope"))
        categories.Weapons.push(mod);
      else if (name.includes("ah-") || name.includes("vehicle") || name.includes("mrzr"))
        categories.Vehicles.push(mod);
      else if (name.includes("clothing") || name.includes("arsenal") || name.includes("loadout"))
        categories.Equipment.push(mod);
      else if (name.includes("effect") || name.includes("tracer") || name.includes("muzzle"))
        categories.Effects.push(mod);
      else if (name.includes("ai")) categories.AI.push(mod);
      else if (name.includes("radio")) categories.Radios.push(mod);
      else if (name.includes("glenwood") || name.includes("frontline"))
        categories["Maps / Scenarios"].push(mod);
      else categories.Other.push(mod);
    });

    Object.keys(categories).forEach(key => {
      if (categories[key].length === 0) delete categories[key];
    });

    return categories;
  };

  /* ---------- SAFE RENDER GUARD ---------- */

  if (!config || !config.game || !Array.isArray(config.game.mods)) {
    return <div className="card">Loading...</div>;
  }

  const installationGroups = groupInstalledMods(config.game.mods, intelligence);

  return (
    <div className="page-stack mods-page">
      <PageHeader title="Mods" description="Manage the game server’s Workshop content.">
        <span className="count-badge">{config.game.mods.length} installed</span>
      </PageHeader>

      <div className="mods-grid">
        <Panel title="Add Single Mod" description="Enter the Workshop details for a mod.">
          <div className="mod-add-grid">
            <Field label="modId" htmlFor="mod-id">
              <input id="mod-id" placeholder="modId" value={newMod.modId}
                onChange={e => setNewMod({ ...newMod, modId: e.target.value })} />
            </Field>
            <Field label="name" htmlFor="mod-name">
              <input id="mod-name" placeholder="name" value={newMod.name}
                onChange={e => setNewMod({ ...newMod, name: e.target.value })} />
            </Field>
            <Field label="version" htmlFor="mod-version">
              <input id="mod-version" placeholder="version" value={newMod.version}
                onChange={e => setNewMod({ ...newMod, version: e.target.value })} />
            </Field>
            <button className="success" onClick={addSingleMod}>Add Mod</button>
          </div>
        </Panel>

        <Panel title="Bulk Replace Mods (Paste Workshop JSON)" description="Replace the entire mod list with the supplied Workshop JSON.">
          <Field label="Workshop JSON" htmlFor="bulk-mods">
            <textarea id="bulk-mods" rows="10" value={bulkMods}
              onChange={e => setBulkMods(e.target.value)} />
          </Field>
          <div className="button-row bulk-actions">
            <button onClick={replaceMods}>Replace Mods</button>
            <button className="danger" onClick={removeAllMods}>Remove ALL Mods</button>
          </div>
        </Panel>

      </div>

      <Panel title="Current Mods" description="Installed Mods · Select a group to view its contents."
        aside={<span className="count-badge">{config.game.mods.length}</span>}>
        {config.game.mods.length === 0 && <div className="empty-state">No mods installed</div>}
        {config.game.mods.length > 0 && intelligence.status === "loading" && <p role="status" className="muted">Checking current scenario requirements…</p>}
        {config.game.mods.length > 0 && intelligence.message && <div className="installed-mods-status">
          <p role="status" className="muted">{intelligence.message}</p>
          <button type="button" className="button-secondary" onClick={() => {
            workshopCache.current.clear();
            setRetry(value => value + 1);
          }}>Retry Workshop lookup</button>
        </div>}
        {INSTALLATION_GROUPS.map(installationGroup => {
          const grouped = groupMods(installationGroups[installationGroup]);
          return <section key={installationGroup} className="installed-mods-section" aria-label={installationGroup}>
            <h3>{installationGroup} <span className="count-badge">{installationGroups[installationGroup].length}</span></h3>
            {!installationGroups[installationGroup].length && <p className="muted">No mods in this section.</p>}
            {Object.keys(grouped).map(group => {
              const groupKey = `${installationGroup}:${group}`;
              return (
                <div key={group} className="mod-group">
                  <button className="mod-group-toggle" aria-expanded={Boolean(expandedGroups[groupKey])}
                    onClick={() => setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }))}>
                    <span><span className="group-arrow" aria-hidden="true">{expandedGroups[groupKey] ? "▾" : "▸"}</span>{group}</span>
                    <span className="count-badge">{grouped[group].length}</span>
                  </button>
                  {expandedGroups[groupKey] && (
                    <div>
                      {grouped[group].map(mod => {
                        const metadata = intelligence.metadata.get(String(mod.modId || "").toUpperCase());
                        const author = mod.author || metadata?.author;
                        const thumbnail = mod.thumbnail || metadata?.thumbnail;
                        const reason = installationGroup === "Required by Current Scenario" ? "Required"
                          : installationGroup === "Optional/User Installed" ? "Optional" : "Unknown";
                        return (
                          <div key={mod.modId} className="mod-row">
                            <div className="installed-mod-identity">
                              {thumbnail && <img className="installed-mod-thumbnail" src={thumbnail} alt={mod.name || metadata?.name || "Workshop thumbnail"} loading="lazy" />}
                              <div className="mod-info"><strong>{mod.name || metadata?.name || metadata?.title || "Unnamed mod"}</strong>
                                <span className="mod-id">Workshop ID: {mod.modId}</span>
                                {author && <span className="muted">By {author}</span>}
                                <span className="installed-mod-reason">Why installed: {reason}</span>
                              </div>
                            </div>
                            <span className="mod-version">Version: {mod.version || "Not set"}</span>
                            <button className="danger" onClick={() => removeMod(mod.modId)}>Remove</button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </section>;
        })}
      </Panel>
    </div>
  );
}
